import { useEffect, useState } from 'react';
import _ from 'lodash';
import styles from './styles.module.scss';
import { SearchBar } from 'src/components/input';
import cx from 'classnames';
import SortByAction from 'src/components/sortByAction';
import { useManageAccessFetch, useStreamAccessFetch, useStreamAnalyticsFetch, useUpdateAccess } from 'src/query/settings/streamAccessManagement';
import { IRecordItem, IUpdateAccess, sortOptions, streamAccessEntityTypes } from 'src/types/settings/streamAccessManagement';
import { Banner as IBanner} from 'src/types/common';
import { Stream as IStream, FetchStreamAnalyticsResponse as IStreamAnalytics } from 'src/types/streams';
import { applySearchAndSort } from 'src/utils/helper';
import { SortOrder } from "src/types/common";
import InfiniteScroll from 'src/components/infiniteScroll';
import Button from 'src/components/button';
import { useSpring, animated } from '@react-spring/web';
import useMeasure from 'react-use-measure';
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import { useStreamAccessAdoption } from 'src/query/settings/adoption/streamAccess';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Text from "src/components/text";
import pluralize from 'pluralize';
import dateFormat from "dateformat";
import Tooltip from "src/components/tooltip";
import Truncate from "src/components/truncate";

const emptyStream = '/images/assets/assetsEmptyMessage.svg';
const noStreamsFound = '/images/settings/streamAccess/noStreamsFound.svg';
const noUsersFound = '/images/settings/streamAccess/noUsersFound.svg';
const emptyStreamBanner = '/images/assets/default_asset.jpg';
const DefaultUserAvatar = '/images/default_user_profile_background.png';

const initialRequestPayload = {
        id: null,
        readAll: null,
        writeAll: null,
        userGroups: [],
        users: []
};

const StreamAccessManagement = () => {
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedStreamId = searchParams.get("id");

  const [requestPayload, setRequestPayload] = useState<IUpdateAccess>(initialRequestPayload);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');

  const { data: streamRecords, isLoading: isLoadingStreams } = useStreamAccessFetch();
  const { data: streamAnalytics, } = useStreamAnalyticsFetch(_.get(requestPayload, "id", '') ?? '');
  const { data: allRecords, hasNextPage: hasNextPageEvents, loadMore: loadMoreEvents, isFetching: isFetchingEvents, refetch: allRecordsFetch, isRefetching: refetchingRecords } = useManageAccessFetch(_.get(requestPayload, "id", '') ?? '', userSearchQuery);
  const { mutate: updateAccessMutate, isPending: isUpdateAccessPending, status: updateAccessStatus, data: updateAccessResult, reset: resetUpdateAccess } = useUpdateAccess(requestPayload?.id ?? '');
  const { trackStreamAccessClicked } = useStreamAccessAdoption();

  const [streams, setStreams] = useState<IStream[]>([]);
  const [orderedStreamEntities, setOrderedStreamEntities] = useState<IRecordItem[]>([]);  // contains user, user group of the selected stream in priority order
  const [streamSearchQuery, setStreamSearchQuery] = useState<string>("");
  const [streamSortActions, setStreamSortActions] = useState<{ sortBy: string; orderBy: SortOrder }>({
    sortBy: "createdDate",
    orderBy: SortOrder.ASC
  });
  const [showSaveChanges, setShowSaveChanges] = useState<boolean>(false);

  const [streamAnalyticsContainerRef, streamAnalyticsContainerBounds] = useMeasure();
  const fadeInAnim = useSpring({
    opacity: _.get(requestPayload, "id", '') ? 1 : 0,
    height: _.get(requestPayload, "id", '') ? streamAnalyticsContainerBounds.height + 30 : 0,
    from: { opacity: 0, height: 0 },
  });

  // Methods
  useEffect(() => {
    if (allRecords && _.size(_.get(allRecords, "pages")) > 0) {
      const listOfRecords = _.flatMap(allRecords.pages, (page) => page.records);
      const orderedStreamEntities = _.orderBy(
        listOfRecords,
        [(record) => record.entityType === "user_group" ? 0 : 1],
        ["asc"] // sort direction
      );
      setOrderedStreamEntities(orderedStreamEntities);
    } else {
      setOrderedStreamEntities([]);
    }
  }, [allRecords]);

  useEffect(() => {
    if (_.size(streamRecords) > 0) {
      const filteredStreams = applySearchAndSort(streamRecords, streamSearchQuery, streamSearchQuery ? ['name'] : [], streamSortActions.sortBy, streamSortActions.orderBy, true); // filter the data based on search query & sort date
      
      if(streamSearchQuery){ // if search query exists, reset the selected stream
            setShowSaveChanges(false); // reset the access change check
            navigate(`/settings/stream-access-management`); // remove
            setRequestPayload(initialRequestPayload); // reset selected stream
      }

      if(_.size(filteredStreams) > 0){
        setStreams(filteredStreams);
      } else {
        setStreams([]);
      }
      return;
    }

    setStreams([]); // empty array
  }, [streamRecords, streamSearchQuery, streamSortActions]);

  useEffect(() => {
      if(updateAccessStatus === 'success'){
         setStreams((prev) => {
              return prev.map((stream) => {
                  if(_.isEqual(stream.id, requestPayload.id)){
                      return {
                        ...stream,
                        readAll: updateAccessResult?.readAll ?? stream.readAll,
                        writeAll: updateAccessResult?.writeAll ?? stream.writeAll,
                      };
                  }
                  return stream;
              })
         });
         resetUpdateAccess(); // reset the mutation state
         setShowSaveChanges(false); // reset save changes
         toast(
           <Toast.Sucess
                        message="Updated Successfully"
                    />, {
                    autoClose: 2000,
          });
      }  
  }, [updateAccessStatus])

  useEffect(() => {
    trackStreamAccessClicked();
  },[]);

  useEffect(() => { // route stream id change
      const existingRequestId = _.get(requestPayload, "id", null);
      if(selectedStreamId && _.size(streams) > 0 && !_.isEqual(existingRequestId, selectedStreamId)){
          const streamRecord = _.find(streams, (stream) => _.isEqual(stream.id, selectedStreamId));
          setRequestPayload({
              id: selectedStreamId,
              readAll: streamRecord?.readAll,
              writeAll: streamRecord?.writeAll, 
              userGroups: [],
              users: []
          })
       };
      setUserSearchQuery(''); // reset user search query
  }, [selectedStreamId, streams]);

  const handleStreamChange = (streamId: string) => {
    setShowSaveChanges(false); // reset the access change check
    if(_.isEqual(_.get(requestPayload, "id", ''), streamId)){ // same
       navigate(`/settings/stream-access-management`); // remove
       setRequestPayload(initialRequestPayload);
    } else {
       navigate(`/settings/stream-access-management?id=${streamId}`); // append the id
    }
  };

  const updateRequestPayload = (record: IRecordItem, access: string, entityType: string) => {
            const prevRequestPayload = _.cloneDeep(requestPayload);
            const payloadEntity = pluralize.plural(entityType); // users or userGroups
            let updatedValues: any = [];
            const existingRecord = _.find(prevRequestPayload[payloadEntity], (r) => r?.[entityType]?.id === record[entityType].id ); // check if prevRequestPayload already exists
            if(existingRecord){ // existing
                  const accessUnchanged = _.isEqual(access === 'read' ? !existingRecord.read : existingRecord.read, record.read) && _.isEqual( access === 'write' ? !existingRecord.write : existingRecord.write, record.write);
                  if(accessUnchanged){ // remove
                    updatedValues = _.filter(prevRequestPayload[payloadEntity], (r) => record[entityType].id !== r?.[entityType]?.id);
                  } else { // update
                     const index = _.findIndex(prevRequestPayload[payloadEntity], (r) => r?.[entityType]?.id === record[entityType].id ); // find its index
                     let updatedRecord = {
                        ...existingRecord,
                         read: access === "read" ? !existingRecord.read : access === "write" ? true : existingRecord.read,
                         write: access === "write" ? !existingRecord.write : existingRecord.write,
                     };
                     updatedValues = prevRequestPayload[payloadEntity].map((item, i) => i === index ? updatedRecord : item);
                  }
            } else { // new
              updatedValues = [
                ...prevRequestPayload[payloadEntity],
                {
                    ...record,
                    read: access === "read" ? !record.read : access === "write" ? true : record.read,
                    write: access === "write" ? !record.write : record.write,
                }
              ]
           }

           return updatedValues;
  }

  const handleAccessChange = (access: string, record: IRecordItem) => {
    if (!showSaveChanges) {
      setShowSaveChanges(!showSaveChanges);
    }

    if (allRecords) {
      const listOfRecords = _.flatMap(allRecords.pages, (page) => page.records);
      const originalRecord = listOfRecords[_.findIndex(listOfRecords, (item) => {
        const recordType = _.get(item, 'entityType', '');
        const recordDetails = recordType === streamAccessEntityTypes.USER ? item[streamAccessEntityTypes.USER] : item[streamAccessEntityTypes.USER_GROUP];
        if (recordType === streamAccessEntityTypes.USER) {
          return recordDetails?.id === record[streamAccessEntityTypes.USER]?.id;
        } else {
          return recordDetails?.id === record[streamAccessEntityTypes.USER_GROUP]?.id;
        }
      })];

      if(originalRecord){
        const recordType  = _.get(originalRecord, 'entityType', ''); // gives user or user_group
        setRequestPayload((prev) => {
          let prevData = _.cloneDeep(prev);
          const entityType = recordType === streamAccessEntityTypes.USER ? streamAccessEntityTypes.USER : streamAccessEntityTypes.USER_GROUP;
          prevData[pluralize.plural(entityType)] = updateRequestPayload(originalRecord, access, entityType);
          return prevData;
        });

      }
      
    }
  };

  const handleBulkAccessChange = (access: string) => {

    if (!showSaveChanges) { 
      setShowSaveChanges(!showSaveChanges);
    }

    setRequestPayload((prev) => {
      let prevData = _.cloneDeep(prev);

      prevData = {
        ...prevData,
        userGroups: _.size(prevData?.userGroups) > 0 ? prevData?.userGroups : [],
        users: _.size(prevData?.users) > 0 ? prevData?.users : [],
        readAll: access === "read" ? !prevData.readAll : access === "write" ? true : prevData.readAll,
        writeAll: access === "write" ? !prevData.writeAll : prevData.writeAll,
      }

      return prevData;
    })
  }

  const handleSaveChanges = () => {
    if (showSaveChanges) {
      // save the access changes
      const updatedRequestPayload = {
            ...requestPayload,
            users: _.map(requestPayload.users, ({ user, ...rest }) => ({
              ...rest,
              user: _.pick(user, ["id"]),
            })),
      };
      updateAccessMutate(updatedRequestPayload);
    }
  };

  const handleResetChanges = () => {
    // reset the access changes
    const findTheStream = _.find(streams, (stream) => _.isEqual(stream.id, requestPayload.id));
    setRequestPayload((prev) => {
        return {
          id: prev.id,
          readAll: findTheStream?.readAll,
          writeAll: findTheStream?.writeAll, 
          userGroups: [],
          users: [] 
        }
     });
    setShowSaveChanges(false);
  };

  return (
    <>
      {!isLoadingStreams ?
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h3 className={styles.title}>Stream Access Management ({_.size(streams)})</h3>
              <p className={styles.subText}>Control who can view and share your content streams.</p>
            </div>
            {showSaveChanges &&
              <div className={styles.action}>
                <p className={styles.content}>click here to</p>
                <Button variant="primary" icon='paperflite-tick' text={isUpdateAccessPending ? 'Updating...' : "Save"} disabled={isUpdateAccessPending} onClick={handleSaveChanges}></Button>
                <Button variant="secondary" text="Cancel" disabled={isUpdateAccessPending} onClick={handleResetChanges}></Button>
              </div>
            }
          </div>

          <div className={styles.contentArea}>
            <div className={styles.leftPane}>

              <animated.div style={fadeInAnim}>
                <div ref={streamAnalyticsContainerRef}>
                  <StreamAnalytics
                    stream={_.find(streams, (stream) => _.isEqual(stream.id, _.get(requestPayload, "id", '')))}
                    analytics={streamAnalytics}
                  />
                </div>
              </animated.div>

              <div className={cx(styles.streamFilter, { [styles.disabledStreamFilter]: _.size(streamRecords) === 0 })}>
                <div className={styles.streamSearchBox}>
                  <SearchBar query={streamSearchQuery} onSearch={(query) => setStreamSearchQuery(query ? query : "")} />
                </div>
                <SortByAction
                  value={streamSortActions}
                  onChange={(val) => setStreamSortActions(val)}
                  sortOptions={sortOptions}
                  classnames={styles.sortByAction}
                />
              </div>

              <div className={styles.streams}>
                {_.size(streamRecords) > 0 ?
                  _.size(streams) > 0 ?
                    <>
                      {_.map(streams, (stream) => (
                        <StreamCard
                          stream={stream}
                          key={stream.id}
                          selected={_.get(requestPayload, "id", '') ? _.isEqual(_.get(requestPayload, "id"), _.get(stream, 'id')) : null}
                          onClick={() => handleStreamChange(stream.id)} />
                      ))}
                    </>
                    :
                    <EmptyResults
                      msg="No streams found."
                      src={noStreamsFound}
                    />
                  :
                  <>
                    <div className={styles.emptyStream}>
                      <img src={emptyStream} alt="empty stream" />
                    </div>
                    <EmptyStreamMessage message='No streams added yet.' />
                    <EmptyStreamMessage />
                  </>}

              </div>
            </div>

            <div className={styles.rightPane}>
              <div className={styles.manageAccessHeader}>
                <h4 className={styles.title}>Manage Access</h4>
                {_.get(requestPayload, "id", '') && _.size(orderedStreamEntities) > 0 && <div className={cx(styles.accessActions, styles.bulkActions)}>
                  <span className={cx(styles.actionItem, {
                    [styles.pointerNone]: requestPayload.writeAll,
                  })}>
                    <i className={cx(styles.icon, {
                      "paperflite-radio-button-unselected": !requestPayload.writeAll && !requestPayload.readAll,
                      [`paperflite-tick-selected ${styles.selectedIcon}`]: requestPayload.writeAll || requestPayload.readAll,
                    })} onClick={() => handleBulkAccessChange('read')}></i>
                    All can view
                  </span>

                  <span className={styles.actionItem}>
                    <i className={cx(styles.icon, {
                      "paperflite-radio-button-unselected": !requestPayload.writeAll,
                      [`paperflite-tick-selected ${styles.selectedIcon}`]: requestPayload.writeAll,
                    })} onClick={() => handleBulkAccessChange('write')}></i>
                    All can edit
                  </span>
                </div>}

              </div>

              {_.get(requestPayload, "id", '') && <div className={styles.usersSearchBox} >
                <SearchBar query={userSearchQuery} onSearch={(query) => setUserSearchQuery(query ? query : "")} />
              </div>}

              {_.get(requestPayload, "id", '') ?
                <>
                  {
                    _.size(orderedStreamEntities) > 0 ?
                      <InfiniteScroll
                        hasMore={hasNextPageEvents}
                        loading={isFetchingEvents}
                        loadMore={loadMoreEvents}
                        loader={() => (
                          <>
                            <EmptyManageAccessPlaceholder isLoading={true} />
                            <EmptyManageAccessPlaceholder isLoading={true} />
                          </>
                        )}
                        className={styles.manageAccessContent}
                      >
                        <>
                          {_.map(orderedStreamEntities, (record, index) =>
                          {
                            const entityType = record?.entityType === streamAccessEntityTypes.USER ? streamAccessEntityTypes.USER : streamAccessEntityTypes.USER_GROUP;
                            const updatedRecord = _.find(requestPayload[pluralize.plural(entityType)], (item) => item?.[entityType]?.id === record?.[entityType]?.id) ?? record;
                            let bulkAccess = '';
                            const stream = _.find(streams, (stream) => _.isEqual(stream.id, selectedStreamId)); 

                            // bulk access 
                            if(requestPayload.writeAll || record.groupWrite) { // prioritize write access
                              bulkAccess = 'write';
                            } else { // then look for read access
                              if(requestPayload.readAll || record.groupRead){
                               bulkAccess = 'read';
                              } 
                            }

                            return record.entityType === streamAccessEntityTypes.USER ?
                              <UserCard
                                user={updatedRecord}
                                key={index}
                                handleClick={(access) => handleAccessChange(access, updatedRecord)}
                                bulkAccess={bulkAccess}
                                stream={stream}
                              />
                              : <UserGroupCard
                                userGroup={updatedRecord}
                                key={index}
                                handleClick={(access) => handleAccessChange(access, updatedRecord)}
                                bulkAccess={bulkAccess}
                               
                              />
})}
                        </>

                      </InfiniteScroll>
                      :
                      <>
                        {userSearchQuery && allRecords && _.size(_.get(allRecords, "pages[0].records")) === 0 &&
                          <EmptyResults
                            msg='No matching users or groups found. <br/> Clear your search to see all users with access.'
                            src={noUsersFound}
                          />
                        }

                        {allRecords === undefined &&
                          <div className={styles.manageAccessContent}>
                            <EmptyManageAccessPlaceholder isLoading={true} />
                            <EmptyManageAccessPlaceholder isLoading={true} />
                          </div>}
                      </>
                  }
                </>
                : <div className={styles.manageAccessContent}>
                  <EmptyManageAccessPlaceholder/>
                  <EmptyManageAccessPlaceholder/>
                  <EmptyManageAccessPlaceholder message='Select a stream to manage access.' />
                </div>
              }

            </div>
          </div>

        </div> :
        <div className={cx(styles.loading)}>
          <p>Loading...</p>
        </div>}
    </>
  );
};


const RenderBanner = ({ stream }: { stream: IStream }) => {

  // Needed a check for logic's
  const renderBg = (banner: IBanner) => {
    const background = _.get(banner, 'background', null);
    if (background) { // background exists
      if (typeof background === 'string') {
        return background;
      } else if (background.full) {
        return background.full;
      }
    }
    return emptyStreamBanner; // default banner
  }

  const renderBanner = () => {
    const type = _.get(stream, 'icon.type', null);
    const banner = _.get(stream, 'banner', null);
    const background = banner && renderBg(banner);
    const backgroundPosition = (banner ? banner.position : null) || 'center center';

    const videoStyle = {
      objectPosition: backgroundPosition,
    }

    const imageStyle = {
      'backgroundImage': `url("${background}")`,
      'backgroundPosition': backgroundPosition
    };

    switch (type) {
      case 'video': // video
        return (
          background &&
          <video
            autoPlay playsInline muted loop
            className={cx(styles.video, backgroundPosition)}
            style={videoStyle}
            key={background}
          >
            <source src={background} type='video/mp4' />
          </video>
        );
      case 'image': // image
        return (
          <div className={styles.image} style={imageStyle}></div>
        );
      default: // other type's or null
        return (
          <div className={styles.image} style={imageStyle}> </div>
        )
    }

  };

  return (
    <div className={styles.bannerContainer}>
      {renderBanner()}
    </div>
  )
}

const StreamCard = ({ stream, selected, onClick }: { stream: IStream, selected?: boolean | null, onClick?: () => void }) => {
  return (
    <div className={cx(styles.streamCard, {
      [styles.unSelected]:  selected !== null && !selected,
    })} onClick={onClick && onClick}>
      <div className={styles.banner}>
        <RenderBanner stream={stream} />
        <span className={styles.assetCount}> {stream.assetCount} </span>
        <i className={cx('paperflite-tick-selected', styles.icon, { [styles.selectedIcon]: selected })} />
      </div>

      <div className={styles.content}>
          <Text text={stream.name} tooltip={{placement:"auto"}} />
      </div>
    </div>
  )
}

const StreamAnalytics = ({ stream, analytics }: { stream?: IStream, analytics: IStreamAnalytics, }) => {

  if (!stream) return <></>; // check if stream exists

  return (
    <div className={styles.streamAnalytics} >
      <div className={styles.bannerContainer}>
        <RenderBanner stream={stream} />
      </div>
      <div className={styles.content}>
        <h3 className={styles.title}> 
           <Text text={`${_.get(stream, 'name', '')}`} tooltip={{placement:"auto"}} />
        </h3>
        <div className={styles.analyticsContainer}>
          <div className={styles.analytics}>
            <div className={styles.analyticsItem}>
              <h5 className={styles.count}>
                {_.get(analytics, 'totalViews', 0)}
              </h5>
              <p className={styles.label}>Views</p>
            </div>
            <div className={styles.analyticsItem}>
              <h5 className={styles.count}>
                {_.get(analytics, 'totalDownloads', 0)}
              </h5>
              <p className={styles.label}>Downloads</p>
            </div>
            <div className={styles.analyticsItem}>
              <h5 className={styles.count}>
                {_.get(analytics, 'assetCount', 0)}
              </h5>
              <p className={styles.label}>Assets</p>
            </div>
          </div>
          <div className={styles.description}>
            <p className={styles.subText}> Owner :  <div className={styles.truncateContent}>
          <Text text={`${_.get(stream, 'createdBy.firstName', '-')} ${_.get(stream, 'createdBy.lastName', '')}`} tooltip={{placement:"auto"}} />
        </div> </p>
            <p className={styles.subText}> Last updated on {dateFormat(stream.modifiedDate, "dd mmm yyyy")}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const UserCard = ({ user, stream, handleClick, bulkAccess }: { user: IRecordItem, stream: any, handleClick?: (type) => void, bulkAccess?: string }) => {
  const userProfile = _.get(user, 'user.icon.full', null);
  const deactivatedUser = !_.get(user, 'user.enabled', true);
  const admin = _.get(user, "user.role", false) === "Administrator" || _.get(user, "user.admin", false);
  const owner = _.get(user, "user.id" , '') === _.get(stream, "createdBy.id") || _.get(user, "user.canCreateStream", false);

  return (
    <div className={cx(styles.userCard, {
      [styles.deactivatedUser]: deactivatedUser
    })}>
      <div className={(styles.content)}>
       {
        deactivatedUser ?  <i className='fa fa-ban'/> :
        <img className={styles.profile} src={userProfile ? userProfile : DefaultUserAvatar} alt='user profile' /> 
        }
        <div className={styles.userInfo}>
          <span>
            <Tooltip
             placement="top" content={`${_.get(user, 'user.firstName', '')} ${_.get(user, 'user.lastName', '')}`}>
              <span>
                <Truncate lines={1} width={owner || admin ? 190 : 240} className={styles.name}>{`${_.get(user, 'user.firstName', '')} ${_.get(user, 'user.lastName', '')}`}</Truncate>
              </span>
            </Tooltip>
          </span>
         <span>
          {deactivatedUser && <span className={styles.name}>(Deactivated)</span>}
          {(owner || admin) && <span className={styles.badge}>{owner ? "Owner" : admin ? "Admin" : ""}</span>}
        </span>
        </div>
      </div>
      { !deactivatedUser &&
        <div className={cx(styles.accessActions, {
          [styles.pointerNone]: admin || owner
        })}>
          <span className={cx(styles.actionItem, {
            [styles.pointerNone]: !admin && !owner && (bulkAccess === 'write' || bulkAccess === 'read') || user.write,
          })}>
            <i className={cx(styles.icon, {
              "paperflite-radio-button-unselected": admin || owner ? false : bulkAccess === '' && !user.write && !user.read,
              [`paperflite-tick-selected ${styles.selectedIcon}`]: admin || owner ? true : bulkAccess === 'read' || bulkAccess === 'write' || (bulkAccess === '' && (user.write || user.read)),
            })} onClick={() => handleClick && handleClick('read')} ></i>
            view
          </span>

          <span className={cx(styles.actionItem, {
            [styles.pointerNone]: bulkAccess === 'write' && !admin && !owner,
          })}>
            <i className={cx(styles.icon, {
              "paperflite-radio-button-unselected": admin || owner ? false : bulkAccess !== 'write' && !user.write,
              [`paperflite-tick-selected ${styles.selectedIcon}`]: admin || owner ? true : bulkAccess === 'write' || user.write,
            })} onClick={() => handleClick && handleClick('write')} ></i>
            edit
          </span>

        </div>
      }
    </div>
  )
}

const UserGroupCard = ({ userGroup, handleClick, bulkAccess }: { userGroup: IRecordItem, handleClick?: (type) => void, bulkAccess?: string }) => {

  const admin = _.get(userGroup, "userGroup.role") === "Administrator" || _.get(userGroup, "userGroup.admin", false);

  // Methods
  const renderUserProfile = () => {
    const totalProfiles = 3;
    const userProfiles = _.get(userGroup, 'userGroup.icons', []) || [];

    return _.range(totalProfiles).map((i) => {
      const profileClass = styles[`profile${i + 1}` as keyof typeof styles];
      const userProfile = userProfiles[i];

      return (
        <img
          key={i}
          className={profileClass}
          src={userProfile?.full || DefaultUserAvatar}
          alt='user profile'
        />
      )
    });
  }

  return (
    <div className={styles.userGroupCard}>
      <div className={styles.content}>
        <div className={styles.groupProfiles}>
          {renderUserProfile()}
        </div>
        <div className={styles.userInfo}>
          <span>
            <Tooltip
             placement="top" content={`${_.get(userGroup, 'userGroup.name', '')}`}>
              <span>
                <Truncate lines={1} width={admin ? 190 : 240} className={styles.name}>{`${_.get(userGroup, 'userGroup.name', '')}`}</Truncate>
              </span>
            </Tooltip>
          </span>
         <span>
            { admin && <span className={styles.badge}>Admin</span>}
         </span>
        </div>
      </div>
      <div className={cx(styles.accessActions, {
        [styles.pointerNone]: admin
      })}>
        <span className={cx(styles.actionItem, {
          [styles.pointerNone]: !admin && (bulkAccess === 'write' || bulkAccess === 'read') || userGroup.write,
        })}>
          <i className={cx(styles.icon, {
            "paperflite-radio-button-unselected": admin ? false : bulkAccess === '' && !userGroup.write && !userGroup.read,
            [`paperflite-tick-selected ${styles.selectedIcon}`]: admin ? true : bulkAccess === 'read' || bulkAccess === 'write' || (bulkAccess === '' && (userGroup.write || userGroup.read)),
          })} onClick={() => handleClick && handleClick('read')}></i>
          view
        </span>

        <span className={cx(styles.actionItem, {
          [styles.pointerNone]: !admin && bulkAccess === 'write',
        })}>
          <i className={cx(styles.icon, {
            "paperflite-radio-button-unselected": admin ? false : bulkAccess !== 'write' && !userGroup.write,
            [`paperflite-tick-selected ${styles.selectedIcon}`]: admin ? true : bulkAccess === 'write' || userGroup.write,
          })} onClick={() => handleClick && handleClick('write')}></i>
          edit
        </span>
      </div>
    </div>
  )
}


const EmptyStreamMessage = ({ message }: { message?: string }) => {
  return (
    <div className={cx(styles.emptyStreamPlaceholder, { [styles.content]: message })}>
      {message && <p className={styles.message}>{message}</p>}
    </div>
  )
};

const EmptyManageAccessPlaceholder = ({ message, isLoading }: { message?: string, isLoading?: boolean }) => {
  return (
    <>
      {!isLoading ? (
        <>
          <div className={styles.emptyManageAccessPlaceholder}>
            <div className={styles.leftContainer}>
              <span className={styles.circle}></span>
              {message ? (
                <p className={styles.message}>{message}</p>
              ) : (
                <span className={cx(styles.line, styles.line_lg)}></span>
              )}
            </div>

            <div className={styles.rightContainer}>
              <span className={cx(styles.line, styles.line_sm)}></span>
              <span className={cx(styles.line, styles.line_sm)}></span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.emptyManageAccessPlaceholder}>
            <div className={styles.leftContainer}>
              <div className={styles.placeholder}>
                <span className={cx(styles.circle, styles.block)}></span>
              </div>
              <div className={styles.placeholder}>
                <span
                  className={cx(styles.line, styles.line_lg, styles.block)}
                ></span>
              </div>
            </div>

            <div className={styles.rightContainer}>
              <div className={styles.placeholder}>
                <span
                  className={cx(styles.line, styles.line_sm, styles.block)}
                ></span>
              </div>

              <div className={styles.placeholder}>
                <span
                  className={cx(styles.line, styles.line_sm, styles.block)}
                ></span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

const EmptyResults = ({ msg, src }: { msg: string, src: string }) => {
  return (
    <div className={styles.noResultFound}>
      <img src={src} alt="no results found" />
      <p className={styles.content} dangerouslySetInnerHTML={{ __html: msg || "" }}></p>
    </div>
  )
}

export default StreamAccessManagement;
